/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace uniform
*/
var uniform = {};

/**
* @name cdf
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/cdf}
*/
import cdf from './../../../../base/dists/uniform/cdf';
setReadOnly( uniform, 'cdf', cdf );

/**
* @name Uniform
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/ctor}
*/
import Uniform from './../../../../base/dists/uniform/ctor';
setReadOnly( uniform, 'Uniform', Uniform );

/**
* @name entropy
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/entropy}
*/
import entropy from './../../../../base/dists/uniform/entropy';
setReadOnly( uniform, 'entropy', entropy );

/**
* @name kurtosis
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/kurtosis}
*/
import kurtosis from './../../../../base/dists/uniform/kurtosis';
setReadOnly( uniform, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/logcdf}
*/
import logcdf from './../../../../base/dists/uniform/logcdf';
setReadOnly( uniform, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/logpdf}
*/
import logpdf from './../../../../base/dists/uniform/logpdf';
setReadOnly( uniform, 'logpdf', logpdf );

/**
* @name mean
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/mean}
*/
import mean from './../../../../base/dists/uniform/mean';
setReadOnly( uniform, 'mean', mean );

/**
* @name median
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/median}
*/
import median from './../../../../base/dists/uniform/median';
setReadOnly( uniform, 'median', median );

/**
* @name mgf
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/mgf}
*/
import mgf from './../../../../base/dists/uniform/mgf';
setReadOnly( uniform, 'mgf', mgf );

/**
* @name pdf
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/pdf}
*/
import pdf from './../../../../base/dists/uniform/pdf';
setReadOnly( uniform, 'pdf', pdf );

/**
* @name quantile
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/quantile}
*/
import quantile from './../../../../base/dists/uniform/quantile';
setReadOnly( uniform, 'quantile', quantile );

/**
* @name skewness
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/skewness}
*/
import skewness from './../../../../base/dists/uniform/skewness';
setReadOnly( uniform, 'skewness', skewness );

/**
* @name stdev
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/stdev}
*/
import stdev from './../../../../base/dists/uniform/stdev';
setReadOnly( uniform, 'stdev', stdev );

/**
* @name variance
* @memberof uniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/uniform/variance}
*/
import variance from './../../../../base/dists/uniform/variance';
setReadOnly( uniform, 'variance', variance );


// EXPORTS //

export default uniform;
